import { Logo } from 'components/common/Logo';
import { useTheme } from 'contexts/ThemeContext';

// Scoped keyframes for the branded loader. Prefixed `an-` so they can't collide
// with anything global; kept in-component so no Tailwind/CSS config changes are
// needed elsewhere.
const BRAND_KEYFRAMES = `
@keyframes an-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes an-glow-pulse {
  0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.75; transform: translate(-50%, -50%) scale(1.16); }
}
@keyframes an-shine {
  0% { background-position: 220% 0; }
  100% { background-position: -40% 0; }
}
@keyframes an-beam {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(350%); }
}
@keyframes an-dot {
  0%, 80%, 100% { transform: scale(0.5); opacity: 0.35; }
  40% { transform: scale(1); opacity: 1; }
}`;

// A light sweep drawn over the logo artwork via `mix-blend-mode` (a
// `background-clip: text` trick only works on text, not an image).
const GLOSS_STYLE = {
  background:
    'linear-gradient(100deg, transparent 38%, rgba(255,255,255,0.9) 50%, transparent 62%)',
  backgroundSize: '220% 100%',
  mixBlendMode: 'overlay',
  animation: 'an-shine 2.4s linear infinite',
};

/** Branded, logo splash shown while a full page/route loads (e.g. on reload). */
function BrandLoader({ label }) {
  const { isDark } = useTheme();
  // Respect a custom label but drive the animated dots ourselves.
  const baseLabel = label.replace(/[.…]+\s*$/, '');

  return (
    <div
      className="relative min-h-screen w-full flex flex-col items-center justify-center gap-8 overflow-hidden bg-paper"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <style>{BRAND_KEYFRAMES}</style>

      {/* Ambient coral glow behind the logo */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[380px] w-[380px] rounded-full bg-brand/25 blur-[90px]"
        style={{ animation: 'an-glow-pulse 3s ease-in-out infinite' }}
      />

      {/* Logo with a glossy light sweep */}
      <div className="relative" style={{ animation: 'an-float 3.2s ease-in-out infinite' }}>
        <span className="relative inline-block">
          <Logo variant={isDark ? 'light' : 'dark'} size={28} />
          <span aria-hidden className="pointer-events-none absolute inset-0" style={GLOSS_STYLE} />
        </span>
      </div>

      {/* Glowing indeterminate progress beam */}
      <span className="relative h-[3px] w-52 overflow-hidden rounded-pill bg-hair">
        <span
          className="absolute inset-y-0 left-0 w-2/5 rounded-pill bg-brand"
          style={{
            animation: 'an-beam 1.3s cubic-bezier(0.65, 0, 0.35, 1) infinite',
            boxShadow: '0 0 12px 1px rgba(255, 122, 102, 0.7)',
          }}
        />
      </span>

      {/* Label with bouncing dots */}
      <span className="flex items-center gap-1 text-sm font-medium text-slate">
        {baseLabel}
        <span className="ml-0.5 flex gap-1">
          {[0, 0.18, 0.36].map((delay) => (
            <i
              key={delay}
              className="h-1 w-1 rounded-full bg-slate"
              style={{ animation: `an-dot 1.2s ease-in-out ${delay}s infinite` }}
            />
          ))}
        </span>
      </span>
    </div>
  );
}

// Inline spinner sizes: [ring box, border thickness]. Full literals so Tailwind
// picks them up at build time.
const SPINNER_SIZES = {
  sm: { box: 'h-5 w-5', border: 'border-2' },
  md: { box: 'h-8 w-8', border: 'border-[3px]' },
  lg: { box: 'h-12 w-12', border: 'border-4' },
};

/**
 * The app's common loader.
 *
 * - `fullscreen` → branded, animated app splash (used on reload / route loads).
 * - otherwise    → a compact on-brand dual-ring spinner with an optional label,
 *   the standard "normal" loader used across the app. `size` is 'sm' | 'md' | 'lg'.
 */
export function Loader({ fullscreen = false, label = 'Loading…', size = 'md' }) {
  if (fullscreen) {
    return <BrandLoader label={label} />;
  }

  const { box, border } = SPINNER_SIZES[size] ?? SPINNER_SIZES.md;

  return (
    <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
      {/* faint full ring + spinning coral arc = crisper than a single ring */}
      <span className={`relative ${box}`}>
        <span className={`absolute inset-0 rounded-full ${border} border-brand/20`} />
        <span
          className={`absolute inset-0 rounded-full ${border} border-transparent border-t-brand animate-spin`}
        />
      </span>
      {label ? <span className="text-sm text-slate">{label}</span> : null}
    </div>
  );
}

export default Loader;
